import { Button, Spinner } from '@heroui/react';
import { Trash2 } from 'lucide-react';
import React, { FC } from 'react';

import { useHeroToast } from '../../../../../hooks';
import {
  AdCreativeListItem,
  useDeleteAdCreatives,
  useFetchAdCreative,
  useGetAdCreatives,
} from '../../../../../queries';
import { findPlatform } from '../../../config/platforms';
import { useAdsGeneratorStore } from '../../../model/store';
import { fromSavedCreative } from '../../../utils/scene';
import { PanelSection } from '../../common';
import { CardHint, CardTitle } from '../../PlatformStep/PlatformStep.styled';
import { EmptyPanelHint, SavedCard, SavedRow } from '../Editor.styled';

/**
 * Zapisane kreacje. Otwarcie podmienia całą planszę razem z formatem, w którym
 * kreacja powstała — dlatego wpis niesie własne wymiary, a nie dopasowuje się
 * do tego, co akurat jest otwarte.
 */
export const LibraryPanel: FC = () => {
  const saved = useAdsGeneratorStore((state) => state.saved);
  const loadScene = useAdsGeneratorStore((state) => state.loadScene);
  const { data: creatives = [], isLoading } = useGetAdCreatives('creative');
  const fetchCreative = useFetchAdCreative();
  const { mutate: removeCreatives } = useDeleteAdCreatives();
  const { showError } = useHeroToast();

  const open = async (creative: AdCreativeListItem): Promise<void> => {
    try {
      const details = await fetchCreative(creative.id);
      loadScene(fromSavedCreative(details), {
        id: details.id,
        name: details.name,
        kind: 'creative',
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : 'Nie udało się otworzyć kreacji');
    }
  };

  return (
    <PanelSection title="Zapisane kreacje">
      {isLoading && <Spinner size="sm" />}
      {!isLoading && creatives.length === 0 && (
        <EmptyPanelHint>
          Biblioteka jest pusta. Zapisz planszę przyciskiem „Zapisz" w górnym pasku, żeby wrócić do niej później.
        </EmptyPanelHint>
      )}
      <div className="flex flex-col gap-2">
        {creatives.map((creative) => (
          <SavedRow key={creative.id}>
            <SavedCard type="button" onClick={() => open(creative)}>
              <img src={creative.preview || undefined} alt="" />
              <span>
                <CardTitle>{creative.name}</CardTitle>
                <CardHint>
                  {`${findPlatform(creative.platformId)?.name ?? creative.platformId}, ${creative.width} x ${creative.height} px`}
                </CardHint>
                {saved?.id === creative.id && <CardHint>Otwarta w edytorze</CardHint>}
              </span>
            </SavedCard>
            <Button
              size="sm"
              radius="sm"
              isIconOnly
              variant="light"
              color="danger"
              aria-label="Usuń kreację"
              onPress={() => removeCreatives(
                { ids: [creative.id] },
                { onError: (error) => showError(error.message || 'Nie udało się usunąć kreacji') },
              )}
            >
              <Trash2 size={14} />
            </Button>
          </SavedRow>
        ))}
      </div>
    </PanelSection>
  );
};
